/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ManagedIdentityCell } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ManagedIdentityCell';
import { ResourceKeyCell } from 'src/appShell/globalHeader/globalHeaderBlades/settingsBlade/ResourceKeyCell';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';

const messages = defineMessages({
    nameColumnHeader: {
        id: 'portal.resourcesTable.nameColumnHeader',
        defaultMessage: 'Resource name'
    },
    regionColumnHeader: {
        id: 'portal.resourcesTable.regionColumnHeader',
        defaultMessage: 'Region'
    },
    managedIdentityColumnHeader: {
        id: 'portal.resourcesTable.managedIdentityColumnHeader',
        defaultMessage: 'Managed identity'
    },
    pricingTierColumnHeader: {
        id: 'portal.resourcesTable.pricingTierColumnHeader',
        defaultMessage: 'Pricing tier'
    },
    resourceKeyColumnHeader: {
        id: 'portal.resourcesTable.resourceKeyColumnHeader',
        defaultMessage: 'Resource key'
    }
});

export const ResourcesTable = observer((props: TableProps<AzureCsAccountStore>) => {
    const { items, sortKey, ...rest } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(rootStore.serviceKey.userRole);

    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 300,
            maxWidth: 600,
            fieldName: 'name',
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            name: localize(messages.nameColumnHeader),
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: AzureCsAccountStore) => <LuisText>{item.name}</LuisText>
        },
        {
            minWidth: 100,
            maxWidth: 100,
            key: 'region',
            isResizable: true,
            fieldName: 'region',
            isSorted: sortKey?.key === 'region',
            name: localize(messages.regionColumnHeader),
            isSortedDescending: sortKey?.key === 'region' && sortKey?.order === 'desc',
            onRender: (item: AzureCsAccountStore) => <LuisText>{item.region}</LuisText>
        },
        {
            minWidth: 120,
            maxWidth: 120,
            isResizable: true,
            key: 'isIdentityEnabled',
            fieldName: 'isIdentityEnabled',
            isSorted: sortKey?.key === 'isIdentityEnabled',
            name: localize(messages.managedIdentityColumnHeader),
            onRender: (item: AzureCsAccountStore) => <ManagedIdentityCell account={item} />,
            isSortedDescending: sortKey?.key === 'isIdentityEnabled' && sortKey?.order === 'desc'
        },
        {
            key: 'sku',
            minWidth: 100,
            maxWidth: 100,
            fieldName: 'sku',
            isResizable: true,
            isSorted: sortKey?.key === 'sku',
            name: localize(messages.pricingTierColumnHeader),
            isSortedDescending: sortKey?.key === 'sku' && sortKey?.order === 'desc',
            onRender: (item: AzureCsAccountStore) => <LuisText>{item.sku.code}</LuisText>
        },
        ...(canChangeAppSettingsAndDetails
            ? [
                  {
                      minWidth: 300,
                      maxWidth: 300,
                      isResizable: true,
                      key: 'keyStrings',
                      fieldName: 'keyStrings',
                      isSorted: sortKey?.key === 'keyStrings',
                      name: localize(messages.resourceKeyColumnHeader),
                      onRender: (item: AzureCsAccountStore) => <ResourceKeyCell account={item} />,
                      isSortedDescending: sortKey?.key === 'keyStrings' && sortKey?.order === 'desc'
                  }
              ]
            : [])
    ];

    return <Table<AzureCsAccountStore> items={items} shimmerLines={3} columns={columns} selectionMode={SelectionMode.single} {...rest} />;
});
